import { ArrowLeftOutlined, CheckOutlined } from '@ant-design/icons'
import { Button, Input, Modal, Select, Table, Tabs } from 'antd'
import { Formik } from 'formik'
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { toast } from 'react-toastify'
import { createSimpleStore } from 'react-simple-reducer'
import { createSelector } from 'reselect'
import styled from '@emotion/styled'
import Axios from 'axios'
import { QuestaoEditor, TIPO_BOTAO } from '../../components/QuestaoEditor/QuestaoEditor'
import { QuestaoModel } from '../../models/QuestaoModel'
import { modulosStore, useModulosDispatch, useModulosState } from '../../stores/modulosStore'

const sanitizeQuestaoParaRevisao = (
  questao: Partial<QuestaoModel> | null | undefined,
  moduloId: number
): QuestaoModel => {
  const local = questao?.local === 'CONTEUDO' ? 'CONTEUDO' : 'AVALIACAO'
  const baseQuestao = new QuestaoModel(local, moduloId)

  return {
    ...baseQuestao,
    ...questao,
    moduloId,
    local,
    questoesAlternativas: questao?.questoesAlternativas || baseQuestao.questoesAlternativas,
  }
}

export const Store = createSimpleStore(
  {
    loadingQuestoes: false,
    questoes: [] as QuestaoModel[],
    questaoRevisao: null as QuestaoModel | null,
  },
  {
    getQuestoesStarted(state) {
      state.loadingQuestoes = true
    },
    getQuestoesSuccess(state, questoes: QuestaoModel[]) {
      state.questoes = questoes
      state.loadingQuestoes = false
    },
    getQuestoesError(state) {
      state.loadingQuestoes = false
    },
    openRevisarQuestao(state, payload: { questao: QuestaoModel; moduloId: number }) {
      state.questaoRevisao = sanitizeQuestaoParaRevisao(payload.questao, payload.moduloId)
    },
    closeRevisarQuestao(state) {
      state.questaoRevisao = null
    },
    novaQuestaoAvaliacao(state, moduloId: number) {
      state.questaoRevisao = sanitizeQuestaoParaRevisao(
        new QuestaoModel('AVALIACAO', moduloId),
        moduloId
      )
    },
  },
  {
    thunks: {
      getQuestoes(moduloId: number) {
        return async (dispatch) => {
          await _getQuestoes(moduloId, dispatch as any)
        }
      },
    },
  }
)

type TDispatch = ReturnType<typeof Store.useDispatch>

async function _getQuestoes(moduloId: number, dispatch: TDispatch) {
  dispatch(Store.actions.getQuestoesStarted())
  try {
    const response = await Axios.get(`/questoes/modulo/${moduloId}`).then((x) => x?.data)
    dispatch(Store.actions.getQuestoesSuccess(response))
  } catch (error) {
    toast(error.response?.data?.message ?? 'Ocorreu um erro ao buscar as questões')
    dispatch(Store.actions.getQuestoesError())
  }
}

type TState = ReturnType<typeof Store.useState>

const selectQuestoesConteudo = createSelector(
  (s: TState) => s.questoes,
  (questoes) => questoes.filter((q) => q.local === 'CONTEUDO')
)

const selectQuestoesAvaliacao = createSelector(
  (s: TState) => s.questoes,
  (questoes) => questoes.filter((q) => q.local === 'AVALIACAO')
)

export const ModulosEditar = () => {
  const dispatch = useModulosDispatch()
  const history = useHistory()
  const { cursoId, moduloId } = useParams<any>()
  const { modulos } = useModulosState()

  useEffect(() => {
    dispatch(modulosStore.thunks.modulosFetch({ cursoId: +cursoId }))
  }, [cursoId, dispatch])

  const modulo = useMemo(
    () =>
      modulos.find((m) => +m.id === +moduloId) ?? {
        id: null,
        nome: '',
      },
    [modulos, moduloId]
  )

  const backToModulos = () => history.goBack()

  return (
    <Store.Provider>
      <Layout>
        <PageHeader>
          <Title>{modulo?.id ? modulo.nome : 'Novo Módulo'}</Title>
          <Button onClick={backToModulos} icon={<ArrowLeftOutlined />}>
            Voltar
          </Button>
        </PageHeader>

        <ContentStack>
          <DadosModulo submitCallback={backToModulos} />
          {modulo?.id ? <Questoes /> : null}
        </ContentStack>
      </Layout>
    </Store.Provider>
  )
}

const BlocoConteudo: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SectionCard>{children || null}</SectionCard>
)

interface DadosModuloProps {
  submitCallback: () => any
}

type TDuracaoUnidade = 'segundos' | 'horas'

const DadosModulo: React.FC<DadosModuloProps> = ({ submitCallback }) => {
  const history = useHistory()
  const { moduloId, cursoId } = useParams<any>()
  const dispatch = useModulosDispatch()
  const { modulos } = useModulosState()
  const modulo = modulos.find((m) => +m.id === +moduloId) ?? {
    id: null,
    nome: '',
    cursoId,
    duracao: '',
  }

  const [duracaoUnidade, setDuracaoUnidade] = useState<TDuracaoUnidade>('segundos')
  const [duracaoDisplay, setDuracaoDisplay] = useState('')

  useEffect(() => {
    setDuracaoUnidade('segundos')
    if (modulo?.duracao === '' || modulo?.duracao == null) {
      setDuracaoDisplay('')
      return
    }
    setDuracaoDisplay(`${modulo.duracao}`)
  }, [modulo?.id, modulo?.duracao])

  const handleVisualizarAulas = () => {
    history.push(`/conteudo/${modulo.id}`)
  }

  return (
    <BlocoConteudo>
      <SectionTitle>Dados do módulo</SectionTitle>
      <Formik
        enableReinitialize
        initialValues={{
          id: modulo.id,
          nome: modulo.nome,
          duracao: modulo.duracao,
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await dispatch(
              modulosStore.thunks.createOrUpdateModulos({ ...modulo, ...values } as any)
            )
            submitCallback()
          } catch (e: any) {
            toast.warn(e?.message ?? 'Ocorreu um erro ao salvar o módulo')
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({ values, handleChange, handleSubmit, isSubmitting, setFieldValue }) => (
          <StyledForm onSubmit={handleSubmit}>
            <FormGrid>
              <FieldGroup className="full-width">
                <FieldLabel>Id</FieldLabel>
                <Input disabled name="id" onChange={handleChange} value={values.id} />
              </FieldGroup>

              <FieldGroup className="full-width">
                <FieldLabel>Nome</FieldLabel>
                <Input name="nome" onChange={handleChange} value={values.nome} />
              </FieldGroup>

              <DuracaoRow className="full-width">
                <FieldGroup className="duracao-left">
                  <FieldLabel>
                    Duração (em {duracaoUnidade === 'horas' ? 'horas' : 'segundos'})
                  </FieldLabel>
                  <Input
                    type="number"
                    min={0}
                    value={duracaoDisplay}
                    onChange={(e) => {
                      const raw = e.target.value
                      setDuracaoDisplay(raw)

                      if (raw === '') {
                        setFieldValue('duracao', '')
                        return
                      }

                      const valorAtual = Number(raw)
                      if (!Number.isFinite(valorAtual)) return
                      const segundosCanonic =
                        duracaoUnidade === 'horas' ? valorAtual * 3600 : valorAtual
                      setFieldValue('duracao', segundosCanonic)
                    }}
                  />
                </FieldGroup>

                <FieldGroup className="duracao-right">
                  <FieldLabel>Unidade da duração</FieldLabel>
                  <Select
                    value={duracaoUnidade}
                    onChange={(novaUnidade: TDuracaoUnidade) => {
                      const valorAtual = Number(duracaoDisplay)
                      const hasValorValido = duracaoDisplay !== '' && Number.isFinite(valorAtual)

                      if (hasValorValido) {
                        const segundosCanonic =
                          duracaoUnidade === 'horas' ? valorAtual * 3600 : valorAtual
                        const novoDisplay =
                          novaUnidade === 'horas' ? segundosCanonic / 3600 : segundosCanonic
                        setDuracaoDisplay(`${novoDisplay}`)
                        setFieldValue('duracao', segundosCanonic)
                      }

                      setDuracaoUnidade(novaUnidade)
                    }}
                    options={[
                      { label: 'Segundos', value: 'segundos' },
                      { label: 'Horas', value: 'horas' },
                    ]}
                  />
                </FieldGroup>
              </DuracaoRow>
            </FormGrid>

            <ModuleAulasActionRow>
              <Button type="primary" onClick={handleVisualizarAulas}>
                Visualizar Todas as Aulas do Módulo
              </Button>
            </ModuleAulasActionRow>

            <FormActions>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={values.nome === '' || isSubmitting}
              >
                Salvar
              </Button>
            </FormActions>
          </StyledForm>
        )}
      </Formik>
    </BlocoConteudo>
  )
}

const Questoes = () => {
  const { moduloId } = useParams<any>()
  const dispatch = Store.useDispatch()
  const numericModuloId = Number(moduloId)
  const [activeTab, setActiveTab] = useState('conteudo')

  useEffect(() => {
    if (!Number.isFinite(numericModuloId) || numericModuloId <= 0) return
    dispatch(Store.thunks.getQuestoes(numericModuloId))
  }, [dispatch, numericModuloId])

  return (
    <>
      <ModalEditarQuestao />
      <BlocoConteudo>
        <SectionTitle>Questões do módulo</SectionTitle>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'conteudo',
              label: 'Conteúdo',
              children: <QuestoesConteudo />,
            },
            {
              key: 'avaliacao',
              label: 'Avaliação',
              children: <QuestoesAvaliacao />,
            },
          ]}
        />
      </BlocoConteudo>
    </>
  )
}

const QuestoesConteudo = () => {
  const state = Store.useState()
  const questoes = selectQuestoesConteudo(state)
  return <ListaQuestoes questoes={questoes} />
}

const QuestoesAvaliacao = () => {
  const { moduloId } = useParams<any>()
  const state = Store.useState()
  const questoes = selectQuestoesAvaliacao(state)
  const dispatch = Store.useDispatch()
  const numericModuloId = Number(moduloId)

  const handleNovaQuestao = () => {
    dispatch(Store.actions.novaQuestaoAvaliacao(numericModuloId))
  }

  return (
    <>
      <Button style={{ marginBottom: 12 }} onClick={handleNovaQuestao}>
        Nova Questão
      </Button>
      <ListaQuestoes questoes={questoes} />
    </>
  )
}

const ListaQuestoes = ({ questoes }: { questoes: QuestaoModel[] }) => {
  const dispatch = Store.useDispatch()
  const { moduloId } = useParams<any>()
  const numericModuloId = Number(moduloId)

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Texto', dataIndex: 'textoQuestao', key: 'textoQuestao' },
    {
      title: 'Revisada',
      dataIndex: 'revisada',
      key: 'revisada',
      width: 100,
      render: (revisada: boolean) =>
        revisada ? <CheckOutlined style={{ color: '#52c41a' }} /> : null,
    },
    {
      title: 'Ações',
      key: 'action',
      width: 120,
      render: (_, questao: QuestaoModel) => (
        <Button
          type="link"
          onClick={() =>
            dispatch(Store.actions.openRevisarQuestao({ questao, moduloId: numericModuloId }))
          }
        >
          Revisar
        </Button>
      ),
    },
  ]

  return (
    <TableWrapper>
      <Table
        columns={columns}
        dataSource={questoes}
        rowKey="id"
        locale={{ emptyText: 'Não há questões cadastradas para este módulo.' }}
        pagination={false}
      />
    </TableWrapper>
  )
}

const ModalEditarQuestao = () => {
  const modulosDispatch = Store.useDispatch()
  const { questaoRevisao } = Store.useState()
  const { moduloId } = useParams<any>()
  const numericModuloId = Number(moduloId)
  const questaoId =
    typeof questaoRevisao?.id === 'number' && Number.isFinite(questaoRevisao.id)
      ? String(questaoRevisao.id)
      : undefined

  const handleClose = () => modulosDispatch(Store.actions.closeRevisarQuestao())

  const handleCloseAndRefresh = () => {
    modulosDispatch(Store.actions.closeRevisarQuestao())
    if (!Number.isFinite(numericModuloId) || numericModuloId <= 0) return
    modulosDispatch(Store.thunks.getQuestoes(numericModuloId))
  }

  if (!questaoRevisao) return null

  return (
    <Modal
      title={questaoRevisao?.id ? 'Editando questão' : 'Nova questão'}
      open={questaoRevisao != null}
      onCancel={handleClose}
      footer={null}
      width={980}
      destroyOnClose
    >
      <QuestaoEditor
        questaoId={questaoId}
        moduloId={numericModuloId}
        local={questaoRevisao?.local}
        tipoQuestao={questaoRevisao?.tipoQuestao}
        onQuestaoSave={handleCloseAndRefresh}
        onQuestaoReview={handleCloseAndRefresh}
        onQuestaoDelete={handleCloseAndRefresh}
        onCancel={handleClose}
        buttons={
          questaoRevisao?.id
            ? [TIPO_BOTAO.SALVAR, TIPO_BOTAO.REVISAR, TIPO_BOTAO.CANCELAR, TIPO_BOTAO.EXCLUIR]
            : [TIPO_BOTAO.SALVAR, TIPO_BOTAO.REVISAR, TIPO_BOTAO.CANCELAR]
        }
      />
    </Modal>
  )
}

const Layout = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  max-width: calc(100vw - 20px);
  min-height: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`

const Title = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 24px;
`

const SectionCard = styled.section`
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  padding: 20px;
`

const SectionTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
  color: #494d50;
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  .full-width {
    grid-column: 1 / -1;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;

    .full-width {
      grid-column: auto;
    }
  }
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .ant-input {
    height: 36px;
    border-radius: 8px;
    width: 100%;
  }

  .ant-select {
    width: 100%;
  }

  .ant-select.ant-select-single {
    height: 36px;
  }

  .ant-select.ant-select-single .ant-select-selector {
    height: 36px !important;
    border-radius: 8px;
    display: flex;
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
  }

  .ant-select.ant-select-single .ant-select-selection-item,
  .ant-select.ant-select-single .ant-select-selection-placeholder {
    line-height: 34px !important;
  }
`

const FieldLabel = styled.label`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
  font-weight: 500;
`

const DuracaoRow = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;

  .duracao-left {
    flex: 1;
  }

  .duracao-right {
    width: 280px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    .duracao-right {
      width: 100%;
    }
  }
`

const ModuleAulasActionRow = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: 8px;
`

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`

const TableWrapper = styled.div`
  background: #ffffff;
  border-radius: 10px;
  overflow-x: auto;

  .ant-table-thead > tr > th {
    background: #ffffff;
    color: #585858;
    font-weight: normal;
    border-bottom: 1px solid #e7e3ec;
    padding: 12px 16px;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #e7e3ec;
    padding: 8px 16px;
  }
`
